import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Menu, ChevronDown, ArrowRight, Layers, Box, Sparkles, Building2, BookOpen,
  Briefcase, Search, Users, Grid3x3, Home, Package, Zap, ShoppingCart, Award,
  Star, Newspaper, GraduationCap, FileText, Mail, Phone, Info,
} from "lucide-react";
import SearchModal from "@/components/SearchModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon-name (from CMS) -> Lucide component. Keep in sync with ICON_OPTIONS
// in AdminNavigationPage.jsx.
const ICON_MAP = {
  layers: Layers, box: Box, sparkles: Sparkles, "building-2": Building2,
  "book-open": BookOpen, briefcase: Briefcase, users: Users, "grid-3x3": Grid3x3,
  home: Home, package: Package, zap: Zap, "shopping-cart": ShoppingCart,
  award: Award, star: Star, newspaper: Newspaper, "graduation-cap": GraduationCap,
  "file-text": FileText, mail: Mail, phone: Phone, info: Info,
};

// Fallback used only if the API returns no navigation (offline / not seeded).
const FALLBACK_NAV = [
  {
    name: "Solutions",
    path: "/solutions",
    icon: Layers,
    dropdown: [
      { name: "Distributors", path: "/solutions/distributors" },
      { name: "Suppliers", path: "/solutions/suppliers" },
      { name: "Decorators", path: "/solutions/decorators" },
      { name: "Print Shops", path: "/solutions/print-shops" },
      { name: "Sign Companies", path: "/solutions/sign-companies" },
      { name: "QuickBooks", path: "/solutions/quickbooks" },
    ],
  },
  {
    name: "Products",
    path: "/products",
    icon: Box,
    dropdown: [
      { name: "Advance", path: "/advance" },
      { name: "Aether", path: "/aether" },
      { name: "Features", path: "/features" },
      { name: "Integrations", path: "/integrations" },
      { name: "Plans", path: "/plans" },
    ],
  },
  {
    name: "Exchange",
    path: "/exchange",
    icon: Sparkles,
    dropdown: [
      { name: "Events", path: "/events" },
      { name: "User Groups", path: "/exchange/user-groups" },
      { name: "Elite Supplier Program", path: "/exchange/elite-supplier" },
      { name: "Antera Academy", path: "/academy" },
    ],
  },
  { name: "Why Antera", path: "/why", icon: Building2 },
  { name: "Insights", path: "/blog", icon: BookOpen },
  { name: "Careers", path: "/careers", icon: Briefcase },
];

// Convert CMS shape `{id,label,path,icon,items}` into internal shape
// `{name,path,icon,dropdown}` used by the existing render code.
function normalizeNav(cmsNav) {
  if (!Array.isArray(cmsNav) || cmsNav.length === 0) return FALLBACK_NAV;
  return cmsNav.map((it) => {
    const subs = Array.isArray(it.items) ? it.items : [];
    const out = {
      name: it.label || it.name || "Untitled",
      path: it.path || "/",
      icon: ICON_MAP[it.icon] || Layers,
    };
    if (subs.length > 0) {
      out.dropdown = subs.map((s) => ({
        name: s.label || s.name || "Untitled",
        path: s.path || "/",
      }));
    }
    return out;
  });
}

function NavDropdown({ item, isActive }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 cursor-default ${
          isActive
            ? "text-sky-600 bg-sky-50"
            : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
        }`}
      >
        {item.name}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={`dropdown-${item.name.toLowerCase().replace(/\s/g, "-")}`}
          className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50"
        >
          {item.dropdown.map((sub) => (
            <Link
              key={sub.path}
              to={sub.path}
              data-testid={`dropdown-item-${sub.name.toLowerCase().replace(/\s/g, "-")}`}
              className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-sky-600 transition-colors"
              onClick={() => setOpen(false)}
            >
              {sub.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [navItems, setNavItems] = useState(FALLBACK_NAV);
  const location = useLocation();

  // Fetch the CMS-driven navigation on mount. Falls back to hardcoded defaults
  // if the API is unreachable or returns an empty list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-settings`);
        if (!cancelled) setNavItems(normalizeNav(data?.navigation));
      } catch {
        // keep FALLBACK_NAV
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // Global keyboard shortcut: Cmd/Ctrl + K opens search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 lg:h-28">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0">
            <img
              src="/api/media/adff924b-451a-45d0-a130-026ae966ce9a"
              alt="Antera Software"
              className="h-12 sm:h-14 lg:h-20 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) =>
              item.dropdown ? (
                <NavDropdown key={item.path} item={item} isActive={isActive(item.path)} />
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive(item.path)
                      ? "text-sky-600 bg-sky-50"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  {item.name}
                </Link>
              )
            )}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="header-search-btn"
              aria-label="Open search"
              className="h-10 w-10 inline-flex items-center justify-center rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              <Search className="h-5 w-5" />
            </button>
            <Link to="/contact">
              <Button
                data-testid="header-contact-btn"
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 text-sm font-semibold"
              >
                Request a Demo
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <div className="lg:hidden flex items-center gap-1">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                data-testid="mobile-header-search-btn"
                aria-label="Open search"
                className="h-10 w-10 inline-flex items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
              >
                <Search className="h-5 w-5" />
              </button>
              <Link to="/contact">
                <Button
                  data-testid="mobile-header-demo-btn"
                  className="bg-sky-600 hover:bg-sky-700 text-white rounded-full h-9 px-4 text-xs font-semibold shadow-sm shadow-sky-600/20"
                >
                  Request a Demo
                </Button>
              </Link>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger" className="h-10 w-10">
                  <Menu className="h-6 w-6 text-slate-800" />
                </Button>
              </SheetTrigger>
            </div>
            <SheetContent
              side="right"
              className="w-[88%] max-w-md p-0 bg-white border-l-0 overflow-hidden"
              data-testid="mobile-nav-drawer"
            >
              {/* Sticky top bar with logo + close (close handled by Sheet automatically) */}
              <div className="px-5 pt-5 pb-3 border-b border-slate-100 bg-white">
                <img
                  src="/api/media/adff924b-451a-45d0-a130-026ae966ce9a"
                  alt="Antera Software"
                  className="h-10 w-auto"
                />
              </div>

              {/* Primary CTA - sticky-friendly at the top */}
              <div className="px-5 py-4 bg-gradient-to-br from-sky-50 to-white border-b border-slate-100">
                <Link to="/contact" onClick={() => setMobileOpen(false)}>
                  <Button
                    data-testid="mobile-contact-btn"
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full h-12 text-sm font-semibold shadow-md shadow-sky-600/20"
                  >
                    Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>

              {/* Scrollable nav list */}
              <nav
                className="flex-1 overflow-y-auto px-3 py-3 pb-32"
                style={{ maxHeight: "calc(100vh - 200px)" }}
              >
                {navItems.map((item) => (
                  <MobileNavItem
                    key={item.name}
                    item={item}
                    isActive={isActive}
                    onNavigate={() => setMobileOpen(false)}
                  />
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Sitewide search modal - mounted here so it covers the entire viewport */}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}

function MobileNavItem({ item, isActive, onNavigate }) {
  const [open, setOpen] = useState(false);
  const Icon = item.icon;
  const active = isActive(item.path);

  // Leaf item (no dropdown)
  if (!item.dropdown) {
    return (
      <Link
        to={item.path}
        onClick={onNavigate}
        data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
        className={`flex items-center gap-3 px-3 py-3.5 rounded-xl text-[15px] font-semibold transition-colors ${
          active ? "text-sky-700 bg-sky-50" : "text-slate-800 hover:bg-slate-50"
        }`}
      >
        {Icon && (
          <span
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              active ? "bg-sky-100 text-sky-600" : "bg-slate-100 text-slate-500"
            }`}
          >
            <Icon className="h-4 w-4" />
          </span>
        )}
        <span>{item.name}</span>
      </Link>
    );
  }

  return (
    <div className="mb-0.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-3 px-3 py-3.5 rounded-xl text-[15px] font-semibold transition-colors ${
          open || active ? "text-sky-700 bg-sky-50" : "text-slate-800 hover:bg-slate-50"
        }`}
      >
        <span className="flex items-center gap-3">
          {Icon && (
            <span
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                open || active ? "bg-sky-100 text-sky-600" : "bg-slate-100 text-slate-500"
              }`}
            >
              <Icon className="h-4 w-4" />
            </span>
          )}
          <span>{item.name}</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="pt-1 pb-2 pl-12 pr-2 space-y-0.5">
          {item.dropdown.map((sub) => (
            <Link
              key={sub.path}
              to={sub.path}
              onClick={onNavigate}
              data-testid={`mobile-nav-sub-${sub.name.toLowerCase().replace(/\s/g, "-")}`}
              className="block px-3 py-2.5 text-sm font-medium text-slate-600 hover:text-sky-600 hover:bg-slate-50 rounded-lg transition-colors"
            >
              {sub.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
